/**
 * Reviews Management - Redesigned with EntityManagement design system
 *
 * لیست نظرات محصولات با اطلاعات کامل: نویسنده، محصول، زمان دقیق (ساعت:دقیقه)،
 * لایک/دیسلایک، تعداد پاسخ‌ها + دیالوگ جزئیات با تایم‌لاین کامل رویدادها
 * (ثبت، ویرایش، پاسخ فروشنده، پاسخ‌های کاربران، تایید/رد ادمین).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InboxIcon from '@mui/icons-material/Inbox';
import BlockIcon from '@mui/icons-material/Block';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';
import PersonIcon from '@mui/icons-material/Person';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StorefrontIcon from '@mui/icons-material/Storefront';
import moment from 'moment-jalaali';

import reviewsService, { Review, ReviewStats } from '../services/reviewsService';
import EventTimeline, { TimelineEvent } from '../components/common/EventTimeline';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import AssigneeCell from '../components/common/AssigneeCell';
import AssigneeFilter, { AssigneeFilterValue } from '../components/common/AssigneeFilter';

import './EntityManagement.css';

type StatusFilter = '' | 'approved' | 'pending' | 'rejected';
type RatingFilter = '' | '1' | '2' | '3' | '4' | '5';

// «در انتظار تایید» فقط وقتی پر می‌شود که تنظیمِ «تأیید دیدگاه قبل از نمایش»
// روشن باشد؛ در حالت پیش‌فرض دیدگاه‌ها بلافاصله تایید می‌شوند.
const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'همه وضعیت‌ها' },
  { value: 'pending', label: 'در انتظار تایید' },
  { value: 'approved', label: 'تایید شده' },
  { value: 'rejected', label: 'رد شده' }
];

const ratingOptions: { value: RatingFilter; label: string }[] = [
  { value: '', label: 'همه امتیازها' },
  { value: '5', label: '۵ ستاره' },
  { value: '4', label: '۴ ستاره' },
  { value: '3', label: '۳ ستاره' },
  { value: '2', label: '۲ ستاره' },
  { value: '1', label: '۱ ستاره' }
];

const formatJalaliDate = (date?: string): string => {
  if (!date) return '-';
  try {
    return toPersianDigits(moment(date).format('jYYYY/jMM/jDD'));
  } catch {
    return '-';
  }
};

const formatTime = (date?: string): string => {
  if (!date) return '';
  try {
    return toPersianDigits(moment(date).format('HH:mm'));
  } catch {
    return '';
  }
};

const formatJalaliDateTime = (date?: string): string => {
  if (!date) return '-';
  try {
    return toPersianDigits(moment(date).format('jYYYY/jMM/jDD — HH:mm'));
  } catch {
    return '-';
  }
};

const getStatusTagClass = (review: Review): string => {
  const status = reviewsService.getStatus(review);
  if (status === 'approved') return 'tag tag-sm tag-success';
  return status === 'pending' ? 'tag tag-sm tag-warning' : 'tag tag-sm tag-danger';
};

const getStatusLabel = (review: Review): string =>
  reviewsService.getStatusLabel(reviewsService.getStatus(review));

const repliesCount = (review: Review): number =>
  (review.userReplies?.length || 0) + (review.sellerReply?.message ? 1 : 0);

const RatingStars: React.FC<{ rating?: number; size?: number }> = ({ rating, size = 14 }) => {
  if (!rating) {
    return (
      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>بدون ستاره</span>
    );
  }
  const stars = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ display: 'inline-flex' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <StarIcon
            key={i}
            style={{
              fontSize: size,
              color: i < stars ? '#f59e0b' : '#e5e7eb'
            }}
          />
        ))}
      </span>
      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
        ({toPersianDigits(String(stars))})
      </span>
    </span>
  );
};

/** نمایش فشردهٔ لایک/دیسلایک */
const VotesBadge: React.FC<{ helpful?: number; unhelpful?: number }> = ({
  helpful = 0,
  unhelpful = 0
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#16a34a', fontSize: 12 }}>
      <ThumbUpAltIcon style={{ fontSize: 15 }} />
      {toPersianDigits(String(helpful))}
    </span>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#dc2626', fontSize: 12 }}>
      <ThumbDownAltIcon style={{ fontSize: 15 }} />
      {toPersianDigits(String(unhelpful))}
    </span>
  </span>
);

/** ساخت رویدادهای تایم‌لاین از روی نظر: ثبت، ویرایش، پاسخ‌ها، مدراسیون */
const buildTimelineEvents = (review: Review): TimelineEvent[] => {
  const events: TimelineEvent[] = [];

  events.push({
    at: review.createdAt,
    category: 'review',
    type: 'review_created',
    title: 'ثبت نظر',
    description: (review.title ? `${review.title}\n` : '') + (review.comment || ''),
    actorRole: 'user',
    actorName: review.userId?.name,
    meta: {
      rating: typeof review.rating === 'number' ? review.rating : undefined,
      helpful: review.helpfulCount || 0,
      unhelpful: review.unhelpfulCount || 0,
      verifiedPurchase: review.isVerifiedPurchase === true ? true : undefined
    }
  });

  if (review.isEdited && review.editedAt) {
    events.push({
      at: review.editedAt,
      category: 'edit',
      type: 'review_edited',
      title: 'ویرایش نظر',
      actorRole: 'user',
      actorName: review.userId?.name
    });
  }

  if (review.sellerReply?.message && review.sellerReply.repliedAt) {
    events.push({
      at: review.sellerReply.repliedAt,
      category: 'reply',
      type: 'seller_reply',
      title: 'پاسخ فروشنده',
      description: review.sellerReply.message,
      actorRole: 'seller',
      actorName: review.sellerReply.repliedBy?.name
    });
    if (review.sellerReply.isEdited && review.sellerReply.editedAt) {
      events.push({
        at: review.sellerReply.editedAt,
        category: 'edit',
        type: 'seller_reply_edited',
        title: 'ویرایش پاسخ فروشنده',
        actorRole: 'seller',
        actorName: review.sellerReply.repliedBy?.name
      });
    }
  }

  (review.userReplies || []).forEach((reply) => {
    if (!reply?.message || !reply.repliedAt) return;
    events.push({
      at: reply.repliedAt,
      category: 'reply',
      type: 'user_reply',
      title: 'پاسخ کاربر',
      description: reply.message,
      actorRole: 'user',
      actorName: reply.repliedBy?.name,
      meta: {
        helpful: reply.helpfulCount || 0,
        unhelpful: reply.unhelpfulCount || 0
      }
    });
    if (reply.isEdited && reply.editedAt) {
      events.push({
        at: reply.editedAt,
        category: 'edit',
        type: 'user_reply_edited',
        title: 'ویرایش پاسخ کاربر',
        actorRole: 'user',
        actorName: reply.repliedBy?.name
      });
    }
  });

  if (review.moderatedAt) {
    events.push({
      at: review.moderatedAt,
      category: 'moderation',
      type: 'moderated',
      title: review.isApproved ? 'تایید نظر توسط ادمین' : 'رد نظر توسط ادمین',
      description: review.rejectionReason || undefined,
      actorRole: 'admin',
      actorName: review.moderatedBy?.name
    });
  }

  return events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
};

const ReviewsManagementPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>('');
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilterValue>('');

  // Stats
  const [stats, setStats] = useState<ReviewStats | null>(null);

  // Dialogs
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [moderateDialogOpen, setModerateDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [moderateAction, setModerateAction] = useState<'approve' | 'reject'>('approve');
  const [rejectReason, setRejectReason] = useState('');
  const [moderating, setModerating] = useState(false);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reviewsService.getReviews(page + 1, rowsPerPage, {
        status: statusFilter,
        rating: ratingFilter ? Number(ratingFilter) : undefined,
        search: searchTerm,
        assignee: assigneeFilter
      });
      setReviews(response.reviews);
      setTotal(response.pagination.total);
    } catch (err) {
      setError('خطا در بارگذاری نظرات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, ratingFilter, searchTerm, assigneeFilter]);

  const loadStats = useCallback(async () => {
    try {
      const s = await reviewsService.getStats();
      setStats(s);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(0);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setStatusFilter('');
    setRatingFilter('');
    setAssigneeFilter('');
    setPage(0);
  };

  const openDetails = (r: Review) => {
    setSelectedReview(r);
    setDetailsOpen(true);
  };

  const openModerate = (r: Review, action: 'approve' | 'reject') => {
    setSelectedReview(r);
    setModerateAction(action);
    setRejectReason('');
    setModerateDialogOpen(true);
  };

  const closeAll = () => {
    setDetailsOpen(false);
    setModerateDialogOpen(false);
    setSelectedReview(null);
  };

  const handleModerate = async () => {
    if (!selectedReview) return;
    if (moderateAction === 'reject' && !rejectReason.trim()) {
      setError('لطفا دلیل رد را وارد کنید');
      return;
    }

    setModerating(true);
    setError(null);
    try {
      await reviewsService.moderateReview(
        selectedReview._id,
        moderateAction,
        moderateAction === 'reject' ? rejectReason : undefined
      );
      setSuccess(moderateAction === 'approve' ? 'نظر تایید شد' : 'نظر رد شد');
      closeAll();
      loadReviews();
      loadStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('خطا در انجام عملیات');
    } finally {
      setModerating(false);
    }
  };

  const totalPages = Math.ceil(total / rowsPerPage);
  const startItem = total === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, total);
  const hasActiveFilters = !!searchTerm || !!statusFilter || !!ratingFilter || !!assigneeFilter;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت نظرات</h1>
          <p className="entity-subtitle">بررسی و تأیید نظرات کاربران</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn refresh"
            onClick={() => {
              loadReviews();
              loadStats();
            }}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {error && (
        <div
          className="tag tag-sm tag-danger"
          style={{ display: 'block', padding: '10px 14px', marginBottom: 12, fontSize: 13 }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              float: 'left',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 16
            }}
          >
            ×
          </button>
        </div>
      )}
      {success && (
        <div
          className="tag tag-sm tag-success"
          style={{ display: 'block', padding: '10px 14px', marginBottom: 12, fontSize: 13 }}
        >
          {success}
          <button
            onClick={() => setSuccess(null)}
            style={{
              float: 'left',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 16
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue">
            <RateReviewIcon />
          </div>
          <div>
            <p className="entity-stat-label">کل نظرات</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats?.total ?? 0))}</p>
            {!!stats?.averageRating && (
              <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: 0 }}>
                میانگین امتیاز: {toPersianDigits(String(stats.averageRating))}
              </p>
            )}
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green">
            <CheckCircleIcon />
          </div>
          <div>
            <p className="entity-stat-label">تایید شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats?.approved ?? 0))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red">
            <BlockIcon />
          </div>
          <div>
            <p className="entity-stat-label">رد شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats?.rejected ?? 0))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange">
            <ChatBubbleOutlineIcon />
          </div>
          <div>
            <p className="entity-stat-label">پاسخ‌ها</p>
            <p className="entity-stat-value">
              {toPersianDigits(String((stats?.totalReplies ?? 0) + (stats?.withSellerReply ?? 0)))}
            </p>
            <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: 0, display: 'flex', gap: 8 }}>
              <span style={{ color: '#16a34a' }}>👍 {toPersianDigits(String(stats?.totalHelpful ?? 0))}</span>
              <span style={{ color: '#dc2626' }}>👎 {toPersianDigits(String(stats?.totalUnhelpful ?? 0))}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی کاربر، محصول یا متن نظر..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setPage(0);
              }}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">امتیاز</label>
            <select
              className="filter-field"
              value={ratingFilter}
              onChange={(e) => {
                setRatingFilter(e.target.value as RatingFilter);
                setPage(0);
              }}
            >
              {ratingOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <AssigneeFilter
            value={assigneeFilter}
            onChange={(v) => {
              setAssigneeFilter(v);
              setPage(0);
            }}
          />

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSearchInput('');
                  }}
                >
                  ×
                </button>
              </span>
            )}
            {statusFilter && (
              <span className="filter-tag">
                وضعیت: {statusOptions.find((o) => o.value === statusFilter)?.label}
                <button onClick={() => setStatusFilter('')}>×</button>
              </span>
            )}
            {ratingFilter && (
              <span className="filter-tag">
                امتیاز: {ratingOptions.find((o) => o.value === ratingFilter)?.label}
                <button onClick={() => setRatingFilter('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>
              پاک کردن همه
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && !reviews.length ? (
          <div className="entity-loading">
            <CircularProgress size={32} />
          </div>
        ) : reviews.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon">
              <InboxIcon style={{ fontSize: 48 }} />
            </div>
            <p className="entity-empty-text">نظری یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>کاربر</th>
                  <th>محصول</th>
                  <th>امتیاز</th>
                  <th>نظر</th>
                  <th>بازخورد</th>
                  <th>پاسخ‌ها</th>
                  <th>وضعیت</th>
                  <th>مسئول</th>
                  <th>تاریخ و ساعت</th>
                  <th style={{ width: 160 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {review.userId?.avatar ? (
                            <img src={review.userId.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                          ) : (
                            <PersonIcon />
                          )}
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{review.userId?.name || 'نامشخص'}</span>
                          <span className="entity-desc" style={{ direction: 'ltr', unicodeBidi: 'embed', fontSize: 11 }}>
                            {review.userId?.phone || review.userId?.email || ''}
                          </span>
                          {review.isVerifiedPurchase && (
                            <span className="tag tag-sm tag-success" style={{ marginTop: 4 }}>
                              خریدار
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ maxWidth: 180 }}>
                      <Tooltip title={review.productId?.name || ''} arrow>
                        <span
                          className="entity-desc"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            maxWidth: 170,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <StorefrontIcon style={{ fontSize: 14, flexShrink: 0, color: 'var(--color-text-secondary)' }} />
                          {review.productId?.name || 'نامشخص'}
                        </span>
                      </Tooltip>
                    </td>
                    <td>
                      <RatingStars rating={review.rating} />
                    </td>
                    <td style={{ maxWidth: 240 }}>
                      <Tooltip title={review.comment || ''} arrow>
                        <span
                          className="entity-desc"
                          style={{
                            display: 'inline-block',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle'
                          }}
                        >
                          {review.comment || '-'}
                        </span>
                      </Tooltip>
                      {review.isEdited && (
                        <span className="tag tag-sm tag-warning" style={{ marginRight: 6 }}>
                          ویرایش شده
                        </span>
                      )}
                    </td>
                    <td>
                      <VotesBadge helpful={review.helpfulCount} unhelpful={review.unhelpfulCount} />
                    </td>
                    <td>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 12,
                          color: repliesCount(review) > 0 ? 'var(--color-text-primary)' : 'var(--color-text-secondary)'
                        }}
                      >
                        <ChatBubbleOutlineIcon style={{ fontSize: 15 }} />
                        {toPersianDigits(String(repliesCount(review)))}
                      </span>
                    </td>
                    <td>
                      <span className={getStatusTagClass(review)}>{getStatusLabel(review)}</span>
                    </td>
                    <td>
                      <AssigneeCell value={review.assignedTo} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span className="entity-desc">{formatJalaliDate(review.createdAt)}</span>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                            fontSize: 11,
                            color: 'var(--color-text-secondary)'
                          }}
                        >
                          <AccessTimeIcon style={{ fontSize: 13 }} />
                          {formatTime(review.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="مشاهده جزئیات و تایم‌لاین" arrow>
                          <button className="action-btn edit" onClick={() => openDetails(review)}>
                            <VisibilityIcon />
                          </button>
                        </Tooltip>
                        {!review.isApproved && (
                          <Tooltip title="تایید" arrow>
                            <button
                              className="action-btn toggle active"
                              onClick={() => openModerate(review, 'approve')}
                            >
                              <CheckCircleIcon />
                            </button>
                          </Tooltip>
                        )}
                        {review.isApproved && (
                          <Tooltip title="رد" arrow>
                            <button
                              className="action-btn delete"
                              onClick={() => openModerate(review, 'reject')}
                            >
                              <CancelIcon />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {reviews.map((review) => (
                <div key={review._id} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      <PersonIcon />
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{review.userId?.name || 'نامشخص'}</div>
                      <div className="entity-card-desc">
                        {review.productId?.name || 'محصول نامشخص'}
                      </div>
                    </div>
                    <div className="entity-card-status">
                      <span className={getStatusTagClass(review)}>{getStatusLabel(review)}</span>
                    </div>
                  </div>
                  <div style={{ padding: '8px 14px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <RatingStars rating={review.rating} />
                    <VotesBadge helpful={review.helpfulCount} unhelpful={review.unhelpfulCount} />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      <ChatBubbleOutlineIcon style={{ fontSize: 14 }} />
                      {toPersianDigits(String(repliesCount(review)))} پاسخ
                    </span>
                    {review.isVerifiedPurchase && (
                      <span className="tag tag-sm tag-success">خریدار</span>
                    )}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      <AccessTimeIcon style={{ fontSize: 13 }} />
                      {formatJalaliDateTime(review.createdAt)}
                    </span>
                  </div>
                  {review.comment && (
                    <div
                      style={{
                        padding: '0 14px 10px',
                        fontSize: 13,
                        color: 'var(--color-text-primary)',
                        lineHeight: 1.7,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {review.comment}
                    </div>
                  )}
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => openDetails(review)}>
                      <VisibilityIcon fontSize="small" /> جزئیات
                    </button>
                    {!review.isApproved && (
                      <button
                        className="entity-card-btn"
                        onClick={() => openModerate(review, 'approve')}
                      >
                        <CheckCircleIcon fontSize="small" /> تایید
                      </button>
                    )}
                    {review.isApproved && (
                      <button
                        className="entity-card-btn danger"
                        onClick={() => openModerate(review, 'reject')}
                      >
                        <CancelIcon fontSize="small" /> رد
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {toPersianDigits(String(startItem))} تا {toPersianDigits(String(endItem))} از{' '}
                {toPersianDigits(String(total))}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>
                  {toPersianDigits(String(page + 1))} / {toPersianDigits(String(totalPages || 1))}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 0}
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={closeAll}
        maxWidth="md"
        fullWidth
        className="review-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>جزئیات نظر</span>
          <button className="details-close" onClick={closeAll}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedReview && (
            <div className="edit-form">
              {/* User Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات کاربر</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام</label>
                      <input
                        type="text"
                        readOnly
                        value={selectedReview.userId?.name || '-'}
                      />
                    </div>
                    <div className="edit-field">
                      <label>موبایل</label>
                      <input
                        type="text"
                        readOnly
                        style={{ direction: 'ltr' }}
                        value={selectedReview.userId?.phone || '-'}
                      />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <input
                        type="text"
                        readOnly
                        style={{ direction: 'ltr' }}
                        value={selectedReview.userId?.email || '-'}
                      />
                    </div>
                    <div className="edit-field">
                      <label>خرید تأیید شده</label>
                      <input
                        type="text"
                        readOnly
                        value={selectedReview.isVerifiedPurchase ? 'بله' : 'خیر'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات محصول</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field full">
                      <label>نام محصول</label>
                      <input
                        type="text"
                        readOnly
                        value={selectedReview.productId?.name || '-'}
                      />
                    </div>
                    <div className="edit-field">
                      <label>اسلاگ</label>
                      <input
                        type="text"
                        readOnly
                        style={{ direction: 'ltr' }}
                        value={selectedReview.productId?.slug || '-'}
                      />
                    </div>
                    <div className="edit-field">
                      <label>شناسه محصول</label>
                      <input
                        type="text"
                        readOnly
                        style={{ direction: 'ltr' }}
                        value={selectedReview.productId?._id || '-'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Review Content */}
              <div className="edit-card">
                <div className="edit-card-header">محتوای نظر</div>
                <div className="edit-card-body">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 16,
                      marginBottom: 12
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>امتیاز:</span>
                      <RatingStars rating={selectedReview.rating} size={18} />
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>بازخورد کاربران:</span>
                      <VotesBadge
                        helpful={selectedReview.helpfulCount}
                        unhelpful={selectedReview.unhelpfulCount}
                      />
                    </span>
                    {selectedReview.isEdited && (
                      <span className="tag tag-sm tag-warning">
                        ویرایش شده {selectedReview.editedAt ? `— ${formatJalaliDateTime(selectedReview.editedAt)}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="edit-form-grid">
                    {selectedReview.title && (
                      <div className="edit-field full">
                        <label>عنوان نظر</label>
                        <input type="text" readOnly value={selectedReview.title} />
                      </div>
                    )}
                    <div className="edit-field full">
                      <label>متن نظر</label>
                      <textarea readOnly value={selectedReview.comment || '-'} rows={4} />
                    </div>
                    <div className="edit-field">
                      <label>تاریخ و ساعت ثبت</label>
                      <input
                        type="text"
                        readOnly
                        value={formatJalaliDateTime(selectedReview.createdAt)}
                      />
                    </div>
                    <div className="edit-field">
                      <label>آخرین بروزرسانی</label>
                      <input
                        type="text"
                        readOnly
                        value={formatJalaliDateTime(selectedReview.updatedAt)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="edit-card">
                <div className="edit-card-header">
                  تایم‌لاین کامل ({toPersianDigits(String(buildTimelineEvents(selectedReview).length))} رویداد)
                </div>
                <div className="edit-card-body">
                  <EventTimeline events={buildTimelineEvents(selectedReview)} />
                </div>
              </div>

              {/* Moderation */}
              <div className="edit-card">
                <div className="edit-card-header">وضعیت بررسی</div>
                <div className="edit-card-body">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 12
                    }}
                  >
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                      وضعیت فعلی:
                    </span>
                    <span className={getStatusTagClass(selectedReview)}>
                      {getStatusLabel(selectedReview)}
                    </span>
                    {!selectedReview.moderatedAt && (
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        (تایید خودکار — هنوز توسط ادمین بررسی نشده)
                      </span>
                    )}
                  </div>
                  {selectedReview.moderatedBy && (
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>بررسی شده توسط</label>
                        <input
                          type="text"
                          readOnly
                          value={selectedReview.moderatedBy?.name || '-'}
                        />
                      </div>
                      <div className="edit-field">
                        <label>تاریخ و ساعت بررسی</label>
                        <input
                          type="text"
                          readOnly
                          value={formatJalaliDateTime(selectedReview.moderatedAt)}
                        />
                      </div>
                    </div>
                  )}
                  {selectedReview.rejectionReason && (
                    <div className="edit-field" style={{ marginTop: 12 }}>
                      <label>دلیل رد</label>
                      <textarea readOnly value={selectedReview.rejectionReason} rows={3} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeAll}>
            بستن
          </button>
          {selectedReview && selectedReview.isApproved && (
            <button
              className="edit-btn cancel"
              style={{ color: '#dc2626', borderColor: '#dc2626' }}
              onClick={() => {
                setDetailsOpen(false);
                openModerate(selectedReview, 'reject');
              }}
            >
              <CancelIcon fontSize="small" /> رد نظر
            </button>
          )}
          {selectedReview && !selectedReview.isApproved && (
            <button
              className="edit-btn submit"
              style={{ background: '#16a34a' }}
              onClick={() => {
                setDetailsOpen(false);
                openModerate(selectedReview, 'approve');
              }}
            >
              <CheckCircleIcon fontSize="small" /> تایید نظر
            </button>
          )}
        </div>
      </Dialog>

      {/* Approve/Reject Confirm Dialog */}
      <Dialog
        open={moderateDialogOpen}
        onClose={closeAll}
        maxWidth="xs"
        fullWidth
        className="review-action-dialog"
      >
        <div className="delete-dialog-content">
          <div
            className="delete-dialog-icon"
            style={{ background: moderateAction === 'approve' ? '#dcfce7' : '#fee2e2' }}
          >
            {moderateAction === 'approve' ? (
              <CheckCircleIcon style={{ fontSize: 28, color: '#16a34a' }} />
            ) : (
              <CancelIcon style={{ fontSize: 28, color: '#dc2626' }} />
            )}
          </div>
          <h3 className="delete-dialog-title">
            {moderateAction === 'approve' ? 'تایید نظر' : 'رد نظر'}
          </h3>
          <p className="delete-dialog-text">
            {moderateAction === 'approve'
              ? 'آیا از تایید این نظر اطمینان دارید؟'
              : 'لطفاً دلیل رد نظر را وارد کنید.'}
          </p>
          {selectedReview && (
            <div
              style={{
                padding: 12,
                background: 'var(--color-bg-default)',
                borderRadius: 8,
                marginBottom: 12,
                textAlign: 'right'
              }}
            >
              <div style={{ marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <RatingStars rating={selectedReview.rating} />
                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {selectedReview.userId?.name || 'نامشخص'} — {formatJalaliDateTime(selectedReview.createdAt)}
                </span>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-primary)',
                  lineHeight: 1.7,
                  maxHeight: 80,
                  overflow: 'auto'
                }}
              >
                {selectedReview.comment || '-'}
              </div>
            </div>
          )}
          {moderateAction === 'reject' && (
            <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="دلیل رد را وارد کنید..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${!rejectReason.trim() ? '#dc2626' : 'var(--color-border)'}`,
                  borderRadius: 8,
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  background: 'var(--color-bg-paper)',
                  color: 'var(--color-text-primary)'
                }}
              />
              {!rejectReason.trim() && (
                <span style={{ color: '#dc2626', fontSize: 12, marginTop: 4, display: 'block' }}>
                  وارد کردن دلیل الزامی است
                </span>
              )}
            </div>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={closeAll} disabled={moderating}>
              انصراف
            </button>
            <button
              className="btn btn-danger"
              style={moderateAction === 'approve' ? { background: '#16a34a' } : undefined}
              onClick={handleModerate}
              disabled={moderating || (moderateAction === 'reject' && !rejectReason.trim())}
            >
              {moderating && <CircularProgress size={16} style={{ color: '#fff' }} />}
              {moderateAction === 'approve' ? (
                <>
                  <CheckCircleIcon fontSize="small" /> تایید
                </>
              ) : (
                <>
                  <CancelIcon fontSize="small" /> رد
                </>
              )}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ReviewsManagementPage;
